import type {QuizSession} from '@cp/model';
import {useCallback, useMemo} from 'react';
import subscribeQuizSessionDoc from '../../api/quizSession/subscribeQuizSessionDoc';
import {useFunctionSubscribe} from '../helper/function/useFunctionSubscribe';
import {useAuth} from '../users/useAuth';

/**
 * Subscribes to the quiz session document so the UI updates in real time when:
 * - The backend trigger populates questions (loading → questions screen)
 * - The user submits answers (question state updates)
 * - The session is ended (endedAt set, remaining questions marked answered with '')
 */
export default function useSubscribeQuizSession(sessionId: string) {
  const user = useAuth();
  const uid = user?.id ?? '';

  const subscribe = useMemo(
    () => (onData: (data: QuizSession | null) => void, onError: (err: Error) => void) =>
      subscribeQuizSessionDoc(uid, sessionId, (s) => onData(s ?? null), onError),
    [uid, sessionId],
  );

  const {data, loading, error} = useFunctionSubscribe<QuizSession | null>(subscribe, {
    skip: !uid || !sessionId,
    deps: [uid, sessionId],
  });

  const refetch = useCallback(() => Promise.resolve(), []);

  return {
    session: data ?? null,
    loading,
    error,
    refetch,
  };
}
